import { AskCustomerButton, CustomerRequestSeed } from '../customerRequests/CustomerRequestComposer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Unit, QuantityInfo } from '../../stores/appOrderHistoryStore';
import { formatQuantityInfo } from '../../utils/quantityUtils';
import { QuantityChangePreview, QuantityChangeStaleError } from '../../services/orderhistory/quantityChangeTypes';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../ui/InventorySlideOver';
import { QuantityChangeReview } from './QuantityChangeReview';
import {
  borderPrimary,
  primaryText,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../styling/stylingHelper';

export interface QuantityInfoOverridePayload {
  stdAmount: number;
  stdMultiplier: number;
  stdUnit: string;
  weightString: string;
}

interface QuantityInfoEditModalProps {
  requestSeed?: CustomerRequestSeed;
  productName: string;
  rawQuantity: number;
  category?: string;
  currentQuantityInfo?: QuantityInfo;
  isOverridden: boolean;
  onPreview: (payload: QuantityInfoOverridePayload | null) => Promise<QuantityChangePreview>;
  onSave: (payload: QuantityInfoOverridePayload, previewFingerprint: string) => Promise<void>;
  onReset: (previewFingerprint: string) => Promise<void>;
  onClose: () => void;
}

const unitShortLabel = (unit: Unit): string => {
  switch (unit) {
    case Unit.GRAM: return 'g';
    case Unit.MILLILITER: return 'ml';
    default: return 'pcs';
  }
};

const inputClass = cn('w-full p-3 border rounded-xl', borderPrimary, baseTextSize, primaryText, 'focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-500');

/** Edit, then review what the change does to recipes and inventory, then confirm. Reset takes the same route. */
const QuantityInfoEditModal = ({
  requestSeed,
  productName,
  rawQuantity,
  category,
  currentQuantityInfo,
  isOverridden,
  onPreview,
  onSave,
  onReset,
  onClose,
}: QuantityInfoEditModalProps) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<string>(currentQuantityInfo?.stdAmount?.toString() ?? '');
  const [multiplier, setMultiplier] = useState<string>(currentQuantityInfo?.stdMultiplier?.toString() ?? '1');
  const [unit, setUnit] = useState<Unit>(currentQuantityInfo?.stdUnit ?? Unit.GRAM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [review, setReview] = useState<{ preview: QuantityChangePreview; payload: QuantityInfoOverridePayload | null } | null>(null);

  const amountNum = parseFloat(amount);
  const multiplierNum = parseFloat(multiplier);
  const valuesValid = !isNaN(amountNum) && amountNum > 0 && !isNaN(multiplierNum) && multiplierNum > 0;

  // Live preview of the resulting total for this order, reusing the display formatter.
  const previewText = valuesValid
    ? formatQuantityInfo(
        rawQuantity,
        { weightString: '', stdAmount: amountNum, stdMultiplier: multiplierNum, stdUnit: unit },
        category
      ).displayText
    : null;

  const buildWeightString = (): string => {
    const short = unitShortLabel(unit);
    const base = multiplierNum > 1 ? `${multiplierNum} × ${amountNum}${short}` : `${amountNum}${short}`;
    return `overridden: ${base}`;
  };

  /** `null` asks what a reset would do. */
  const openReview = async (payload: QuantityInfoOverridePayload | null) => {
    setIsSubmitting(true);
    setError('');
    try {
      setReview({ preview: await onPreview(payload), payload });
    } catch (failure) {
      console.error('Failed to preview quantity change:', failure);
      setError(t('orderHistory.quantityChange.previewFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirm = async () => {
    if (!review || review.preview.notConverted) return;
    setIsSubmitting(true);
    setError('');
    try {
      if (review.payload) await onSave(review.payload, review.preview.fingerprint);
      else await onReset(review.preview.fingerprint);
      onClose();
    } catch (failure) {
      console.error('Failed to apply quantity change:', failure);
      if (failure instanceof QuantityChangeStaleError) {
        // Something changed underneath the review, so what's on screen is no longer what would happen.
        setError(t('orderHistory.quantityChange.stale'));
        try {
          setReview({ ...review, preview: await onPreview(review.payload) });
        } catch (reloadFailure) {
          console.error('Failed to refresh quantity change preview:', reloadFailure);
          setReview(null);
        }
      } else {
        setError(t(review.payload ? 'orderHistory.quantityInfoEdit.saveFailed' : 'orderHistory.quantityInfoEdit.resetFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorLine = error && <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>;

  if (review) {
    return <InventorySlideOver isOpen onClose={isSubmitting ? () => undefined : onClose} closeLabel={t('common.close')}
      title={t('orderHistory.quantityChange.reviewTitle')} subtitle={productName}
      footer={<>
        <button type="button" disabled={isSubmitting} className={slideOverCancelClass} onClick={() => { setReview(null); setError(''); }}>{t('common.back')}</button>
        <button type="button" disabled={isSubmitting || review.preview.notConverted} className={slideOverPrimaryClass} onClick={() => void confirm()}>
          {isSubmitting ? t('orderHistory.quantityInfoEdit.saving') : t('orderHistory.quantityChange.confirm')}
        </button>
      </>}>
      <QuantityChangeReview preview={review.preview} />
      {errorLine}
    </InventorySlideOver>;
  }

  return <InventorySlideOver isOpen onClose={isSubmitting ? () => undefined : onClose} closeLabel={t('common.close')}
    title={t('orderHistory.quantityInfoEdit.title')} subtitle={productName}
    footer={<>
      {isOverridden && <button type="button" disabled={isSubmitting} className={slideOverCancelClass} onClick={() => void openReview(null)}>
        {t('orderHistory.quantityInfoEdit.resetToDefault')}
      </button>}
      <button type="button" disabled={isSubmitting || !valuesValid} className={slideOverPrimaryClass}
        onClick={() => void openReview({ stdAmount: amountNum, stdMultiplier: multiplierNum, stdUnit: unit, weightString: buildWeightString() })}>
        {isSubmitting ? t('orderHistory.quantityChange.loading') : t('orderHistory.quantityChange.reviewButton')}
      </button>
    </>}>
    {requestSeed && <AskCustomerButton className="mb-3" seed={{ ...requestSeed, type: 'product_quantity', productName }} />}

    <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:grid-cols-[1fr_auto_1fr_1fr]">
      <div>
        <label htmlFor="quantity-multiplier" className={cn(mediumText, smallTextSize, secondaryText, 'mb-1 block')}>{t('orderHistory.quantityInfoEdit.multiplierLabel')}</label>
        <input id="quantity-multiplier" type="number" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} step="1" min="1" className={inputClass} />
      </div>
      <div className="flex items-end pb-3 text-lg font-semibold text-muted dark:text-muted-dark">×</div>
      <div>
        <label htmlFor="quantity-amount" className={cn(mediumText, smallTextSize, secondaryText, 'mb-1 block')}>{t('orderHistory.quantityInfoEdit.amountLabel')}</label>
        <input id="quantity-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} step="0.01" min="0" autoFocus className={inputClass} />
      </div>
      <div className="col-span-3 sm:col-span-1">
        <label htmlFor="quantity-unit" className={cn(mediumText, smallTextSize, secondaryText, 'mb-1 block')}>{t('orderHistory.quantityInfoEdit.unitLabel')}</label>
        <select id="quantity-unit" value={unit} onChange={(e) => setUnit(e.target.value as Unit)} className={cn(inputClass, whiteBG)}>
          <option value={Unit.PIECE}>{t('orderHistory.quantityInfoEdit.unitPieces')}</option>
          <option value={Unit.GRAM}>{t('orderHistory.quantityInfoEdit.unitGrams')}</option>
          <option value={Unit.MILLILITER}>{t('orderHistory.quantityInfoEdit.unitMl')}</option>
        </select>
      </div>
    </div>

    {previewText && <p className={cn(smallTextSize, secondaryText, 'mt-3')}>{t('orderHistory.quantityInfoEdit.preview', { ordered: rawQuantity, total: previewText })}</p>}
    {!valuesValid && (amount !== '' || multiplier !== '') && <p className={cn(smallTextSize, secondaryText, 'mt-3')}>{t('orderHistory.quantityInfoEdit.invalidValues')}</p>}
    {errorLine}
  </InventorySlideOver>;
};

export default QuantityInfoEditModal;
